// Page « Lieux » (transport, lot T1b-1) : le carnet des adresses qui reviennent (aéroport, gare, hôtel…).
// Liste avec recherche, ajout, modification, retrait du carnet (un lieu retiré n'est plus proposé pour une course)
// et suppression après confirmation (refusée par le serveur si le lieu a déjà servi : on le retire alors du carnet).
// Carnet vide : bouton « Ajouter les lieux courants » (liste du fichier backend/lieux_courants.json, jamais ajoutée
// automatiquement).
import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PLACE_KINDS, norm } from "@/lib/places";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Archive, ArchiveRestore, MapPin, Search, ListPlus } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", address: "", kind: "other", access_notes: "" };

export default function Places() {
  const [items, setItems] = useState(null); // null = pas encore chargé (le bouton « lieux courants » attend)
  const [common, setCommon] = useState([]); // lieux courants proposés
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [form, setForm] = useState(EMPTY);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => { api.get("/places").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(() => {
    load();
    api.get("/places/common").then((r) => setCommon(r.data)).catch(() => setCommon([]));
  }, [load]);

  const open = (item) => {
    setForm(item ? { ...EMPTY, ...item, access_notes: item.access_notes || "" } : EMPTY);
    setDialog({ open: true, item });
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const nameMissing = !form.name.trim();
  const addressMissing = !form.address.trim();

  const save = async () => {
    if (nameMissing || addressMissing) return;
    setSaving(true);
    const body = { name: form.name, address: form.address, kind: form.kind, access_notes: form.access_notes };
    try {
      if (dialog.item) await api.patch(`/places/${dialog.item.id}`, body);
      else await api.post("/places", body);
      toast.success(dialog.item ? "Lieu modifié" : "Lieu ajouté");
      setDialog({ open: false, item: null }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const setActive = async (p, active) => {
    try {
      await api.patch(`/places/${p.id}`, { active });
      toast.success(active ? "Lieu remis dans le carnet" : "Lieu retiré du carnet"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const confirmDelete = async () => {
    const p = toDelete; setToDelete(null);
    try { await api.delete(`/places/${p.id}`); toast.success("Lieu supprimé"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addCommon = async () => {
    setAdding(true);
    try {
      const { data } = await api.post("/places/common");
      toast.success(data.added > 1 ? `${data.added} lieux ajoutés au carnet` : data.added === 1 ? "1 lieu ajouté au carnet" : "Ces lieux sont déjà dans le carnet");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setAdding(false); }
  };

  // Ordre alphabétique français (« Aéroport » avant « Anantara », « Éden » avec les E) : la base trie les accents à part.
  const list = [...(items || [])].sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }));
  const shown = q.trim() ? list.filter((p) => norm(`${p.name} ${p.address}`).includes(norm(q))) : list;
  const actifs = shown.filter((p) => p.active !== false);
  const retires = shown.filter((p) => p.active === false);
  const Row = ({ p }) => (
    <div className="p-4 md:p-5 flex flex-wrap items-center gap-4" data-testid={`place-row-${p.id}`}>
      <div className="min-w-0 flex-1 max-md:basis-full">
        <div className="flex items-center gap-3 flex-wrap gap-y-1">
          <span className="font-display font-bold break-words min-w-0">{p.name}</span>
          <span className="px-2 py-0.5 text-xs rounded-full border border-border whitespace-nowrap" data-testid={`place-kind-${p.id}`}>{PLACE_KINDS[p.kind] || PLACE_KINDS.other}</span>
          {p.active === false && <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground whitespace-nowrap" data-testid={`place-retired-${p.id}`}>Retiré du carnet</span>}
        </div>
        <div className="text-sm text-muted-foreground mt-1 break-words" data-testid={`place-address-${p.id}`}>{p.address}</div>
        {p.access_notes && <div className="text-xs text-muted-foreground mt-1 break-words whitespace-pre-line" data-testid={`place-notes-${p.id}`}>Accès : {p.access_notes}</div>}
      </div>
      <div className="flex items-center gap-1">
        {p.active === false
          ? <Button size="sm" variant="outline" className="gap-1" onClick={() => setActive(p, true)} data-testid={`place-restore-${p.id}`}><ArchiveRestore className="h-3.5 w-3.5" /> Remettre</Button>
          : <Button size="sm" variant="outline" className="gap-1" onClick={() => setActive(p, false)} data-testid={`place-retire-${p.id}`}><Archive className="h-3.5 w-3.5" /> Retirer</Button>}
        <Button size="icon" variant="ghost" onClick={() => open(p)} title="Modifier" data-testid={`place-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => setToDelete(p)} title="Supprimer" data-testid={`place-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent">Carnet</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Lieux</h1>
        </div>
        <Button className="gap-2" onClick={() => open(null)} data-testid="place-new"><Plus className="h-4 w-4" /> Nouveau lieu</Button>
      </div>

      {list.length > 0 && (
        <div className="relative max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un nom ou une adresse" className="pl-9" data-testid="place-search" />
        </div>
      )}

      {items && list.length === 0 && (
        <div className="bg-card border border-border rounded-2xl shadow-card p-6 space-y-4" data-testid="places-empty">
          <div className="text-sm text-muted-foreground flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0" /> Le carnet est vide. Ajoutez les adresses qui reviennent : vous les retrouverez en quelques lettres dans la prise en charge et la dépose d'une course.</div>
          {common.length > 0 && (
            <div className="space-y-2">
              <Button variant="outline" className="gap-2" onClick={addCommon} disabled={adding} data-testid="places-add-common"><ListPlus className="h-4 w-4" /> Ajouter les lieux courants</Button>
              <p className="text-xs text-muted-foreground">{`${common.length} lieux de Monaco et de la Côte d'Azur (aéroports, gares, ports, grands hôtels et restaurants). Vous pourrez les modifier ou les retirer ensuite.`}</p>
            </div>
          )}
        </div>
      )}

      {list.length > 0 && (
        <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden divide-y divide-border">
          {actifs.length === 0 && <div className="p-6 text-sm text-muted-foreground">Aucun lieu ne correspond.</div>}
          {actifs.map((p) => <Row key={p.id} p={p} />)}
        </div>
      )}
      {retires.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-overline text-muted-foreground">Retirés du carnet</div>
          <div className="bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border opacity-80">
            {retires.map((p) => <Row key={p.id} p={p} />)}
          </div>
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{dialog.item ? "Modifier le lieu" : "Nouveau lieu"}</DialogTitle></DialogHeader>
          <div className="space-y-4 min-w-0">
            <div className="space-y-2"><Label>Nom court</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex. Hôtel de Paris" data-testid="place-name" />
              {nameMissing && <p className="text-xs text-destructive" data-testid="place-name-error">Le nom court est obligatoire.</p>}
            </div>
            <div className="space-y-2"><Label>Adresse complète</Label>
              <Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Ex. Place du Casino, 98000 Monaco" data-testid="place-address" />
              {addressMissing && <p className="text-xs text-destructive" data-testid="place-address-error">L'adresse est obligatoire.</p>}
            </div>
            <div className="space-y-2"><Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                <SelectTrigger data-testid="place-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PLACE_KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>Consignes d'accès (facultatif)</Label>
              <Textarea value={form.access_notes} onChange={(e) => set("access_notes", e.target.value)} placeholder="Ex. portail côté mer, sonner au gardien…" data-testid="place-notes" /></div>
            <p className="text-xs text-muted-foreground">Sur une course, le nom court s'affiche partout ; l'adresse sert à la carte et à la déclaration de course à Monaco. Le chauffeur voit le nom, l'adresse et les consignes d'accès des lieux de ses courses.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, item: null })} className="rounded-full">Annuler</Button>
            <Button onClick={save} disabled={nameMissing || addressMissing || saving} className="rounded-full" data-testid="place-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{`Supprimer ${toDelete ? `« ${toDelete.name} »` : "ce lieu"} ?`}</AlertDialogTitle>
            <AlertDialogDescription>Cette action est définitive. Un lieu qui a déjà servi pour une course ne se supprime pas : retirez-le plutôt du carnet.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-confirm-no">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm-yes">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
